import React, { useId, useState, useEffect } from 'react'
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { SubscriptionTier } from '../../types'
import useEscapeKey from '../../hooks/useEscapeKey'
import Spinner from '../Spinner'
import FeeEstimate, { FeeRates } from './FeeEstimate'

interface CreateTierModalProps {
  isOpen: boolean
  onClose: () => void
  /** Resolves with the saved tier (to show whether it's live on Stripe), or rejects with the error. */
  onSave: (tier: Partial<SubscriptionTier>) => Promise<SubscriptionTier | void>
  tier?: SubscriptionTier
  /** For the "you receive about" estimate; null: "fees shown at payout". */
  feeRates?: FeeRates | null
}

const toCents = (value: string): number | null => {
  const n = parseFloat(value)
  return isNaN(n) ? null : Math.round(n * 100)
}

const CreateTierModal: React.FC<CreateTierModalProps> = ({
  isOpen,
  onClose,
  onSave,
  tier,
  feeRates = null,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_monthly: '',
    price_yearly: '',
    features: [''],
    is_active: true,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<SubscriptionTier | null>(null)
  const titleId = useId()

  useEscapeKey(onClose, isOpen)

  useEffect(() => {
    if (tier) {
      setFormData({
        name: tier.name,
        description: tier.description,
        price_monthly: (tier.price_monthly_cents / 100).toFixed(2),
        price_yearly:
          tier.price_yearly_cents != null ? (tier.price_yearly_cents / 100).toFixed(2) : '',
        features: tier.features.length > 0 ? tier.features : [''],
        is_active: tier.is_active,
      })
    } else {
      setFormData({
        name: '',
        description: '',
        price_monthly: '',
        price_yearly: '',
        features: [''],
        is_active: true,
      })
    }
    setError(null)
    setSaved(null)
  }, [tier, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.name || !formData.description || !formData.price_monthly) {
      setError('Please fill in all required fields')
      return
    }

    const priceMonthly = parseFloat(formData.price_monthly)
    if (isNaN(priceMonthly) || priceMonthly <= 0) {
      setError('Monthly price must be a valid positive number')
      return
    }

    let priceYearlyCents: number | undefined
    if (formData.price_yearly.trim() !== '') {
      const priceYearly = parseFloat(formData.price_yearly)
      if (isNaN(priceYearly) || priceYearly <= 0) {
        setError('Yearly price must be a valid positive number')
        return
      }
      priceYearlyCents = Math.round(priceYearly * 100)
    }

    const validFeatures = formData.features.filter((f) => f.trim() !== '')
    if (validFeatures.length === 0) {
      setError('Please add at least one feature')
      return
    }

    setIsLoading(true)
    try {
      const result = await onSave({
        name: formData.name,
        description: formData.description,
        price_monthly_cents: Math.round(priceMonthly * 100),
        price_yearly_cents: priceYearlyCents,
        features: validFeatures,
        is_active: formData.is_active,
      })
      if (result) {
        setSaved(result) // stay open to say whether it's live on Stripe
      } else {
        onClose()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save tier')
    } finally {
      setIsLoading(false)
    }
  }

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ''],
    }))
  }

  const removeFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }))
  }

  const updateFeature = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.map((f, i) => (i === index ? value : f)),
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">
            {tier ? 'Edit Subscription Tier' : 'Create Subscription Tier'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {saved ? (
          <div className="p-6 space-y-4" role="status">
            {saved.stripe_price_id_monthly ? (
              <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 px-4 py-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-green-800">Live on Stripe ✓</p>
                  <p className="text-sm text-green-700 mt-1">
                    {saved.name} can take subscribers at ${(saved.price_monthly_cents / 100).toFixed(2)}/month
                    {saved.price_yearly_cents != null &&
                      ` or $${(saved.price_yearly_cents / 100).toFixed(2)}/year`}
                    .
                  </p>
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                Saved, but this tier isn't on Stripe yet, so nobody can subscribe. Try saving again.
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="tier-name" className="block text-sm font-medium text-gray-700 mb-2">
              Tier Name *
            </label>
            <input
              id="tier-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Premium, VIP, Supporter"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label htmlFor="tier-description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="tier-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what subscribers will get"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="tier-price-monthly" className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Price *
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  $
                </span>
                <input
                  id="tier-price-monthly"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                  placeholder="9.99"
                  className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="mt-1">
                <FeeEstimate priceCents={toCents(formData.price_monthly)} per="month" rates={feeRates} />
              </div>
            </div>

            <div>
              <label htmlFor="tier-price-yearly" className="block text-sm font-medium text-gray-700 mb-2">
                Yearly Price (optional)
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  $
                </span>
                <input
                  id="tier-price-yearly"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_yearly}
                  onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                  placeholder="99.99"
                  className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {feeRates && (
                <div className="mt-1">
                  <FeeEstimate priceCents={toCents(formData.price_yearly)} per="year" rates={feeRates} />
                </div>
              )}
            </div>
          </div>

          {tier && (
            <p className="text-xs text-gray-600 -mt-3">
              Changing a price only affects new subscribers. Existing subscribers keep the price
              they signed up at.
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Features *
            </label>
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    placeholder="e.g., Access to exclusive content"
                    aria-label={`Feature ${index + 1}`}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {formData.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label={`Remove feature ${index + 1}`}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addFeature}
              className="mt-2 flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Feature
            </button>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
              Make this tier available to subscribers
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && <Spinner size="sm" className="border-white border-t-transparent" />}
              {isLoading ? 'Saving' : tier ? 'Update Tier' : 'Create Tier'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  )
}

export default CreateTierModal
